import { api } from './api';

const PDF_ENDPOINT = '/staff/badges/export.pdf';
const CSV_ENDPOINT = '/staff/badges/export.csv';

function withParams(endpoint: string, userIds?: string[]): string {
  if (!userIds || userIds.length === 0) return endpoint;
  const search = new URLSearchParams({ userIds: userIds.join(',') });
  return `${endpoint}?${search.toString()}`;
}

export async function fetchBadgePdfBlob(userIds?: string[]): Promise<Blob> {
  return api.get<Blob>(withParams(PDF_ENDPOINT, userIds), { responseType: 'blob' });
}

export async function fetchBadgeCsvBlob(userIds?: string[]): Promise<Blob> {
  return api.get<Blob>(withParams(CSV_ENDPOINT, userIds), { responseType: 'blob' });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export async function downloadBadgesPdf(userIds?: string[]): Promise<void> {
  const blob = await fetchBadgePdfBlob(userIds);
  triggerDownload(blob, `converge-nps-badges-${todayStamp()}.pdf`);
}

export async function downloadBadgesCsv(userIds?: string[]): Promise<void> {
  const blob = await fetchBadgeCsvBlob(userIds);
  triggerDownload(blob, `converge-nps-badges-${todayStamp()}.csv`);
}

export async function createBadgePreviewUrl(userIds: string[]): Promise<string> {
  const blob = await fetchBadgePdfBlob(userIds);
  return URL.createObjectURL(blob);
}

export async function printBadgePdf(userIds: string[]): Promise<void> {
  const blob = await fetchBadgePdfBlob(userIds);
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');

  const cleanup = () => {
    URL.revokeObjectURL(url);
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  await new Promise<void>((resolve, reject) => {
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) throw new Error('Iframe contentWindow unavailable');
        win.focus();
        win.print();
        // Most browsers don't fire afterprint in iframes reliably — clean up on a timer.
        setTimeout(cleanup, 60_000);
        resolve();
      } catch (err) {
        cleanup();
        reject(err);
      }
    };
    iframe.onerror = () => {
      cleanup();
      reject(new Error('Failed to load badge PDF for printing'));
    };
    iframe.src = url;
    document.body.appendChild(iframe);
  });
}
